import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Plus,
  Users,
  Eye,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = ['ACADEMY', 'FIRST_TEAM', 'B_TEAM', 'WOMEN', 'OTHER'];
const AGE_GROUPS = ['U14', 'U15', 'U16', 'U17', 'U19', 'SENIOR'];

const TeamListPage = () => {
  const { api, isAdmin } = useAuth();
  const { t } = useLanguage();

  const [teams, setTeams] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Create dialog
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newTeam, setNewTeam] = useState({
    name: '',
    code: '',
    age_group: '',
    category: 'ACADEMY',
    season: '2024-2025',
    description: '',
  });
  const [isCreating, setIsCreating] = useState(false);

  const fetchTeams = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.get('/teams');
      setTeams(response.data);
    } catch (err) {
      setError('Failed to load teams');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    setIsCreating(true);
    try {
      await api.post('/teams', newTeam);
      toast.success(t('saveSuccess'));
      setShowCreateDialog(false);
      setNewTeam({
        name: '',
        code: '',
        age_group: '',
        category: 'ACADEMY',
        season: '2024-2025',
        description: '',
      });
      fetchTeams();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error'));
      console.error(err);
    } finally {
      setIsCreating(false);
    }
  };

  const getCategoryBadge = (category) => {
    const colors = {
      ACADEMY: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
      FIRST_TEAM: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
      B_TEAM: 'bg-sky-500/10 text-sky-500 border-sky-500/20',
      WOMEN: 'bg-pink-500/10 text-pink-500 border-pink-500/20',
      OTHER: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
    };
    return (
      <span className={`px-2 py-1 text-xs font-semibold uppercase rounded-sm border ${colors[category] || colors.OTHER}`}>
        {category}
      </span>
    );
  };

  return (
    <div className="space-y-6 fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-100">{t('teamList')}</h1>
          <p className="text-sm text-zinc-500 mt-1">
            {teams.length} {t('teams').toLowerCase()}
          </p>
        </div>
        {isAdmin && (
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="bg-amber-500 text-zinc-950 hover:bg-amber-400 font-bold uppercase tracking-wide rounded-sm"
            data-testid="add-team-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('addTeam')}
          </Button>
        )}
      </div>

      {/* Teams Grid */}
      {isLoading ? (
        <div className="p-8 text-center text-zinc-500">{t('loading')}</div>
      ) : error ? (
        <div className="p-8 text-center text-rose-400 flex items-center justify-center gap-2">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      ) : teams.length === 0 ? (
        <div className="p-8 text-center text-zinc-500">{t('noResults')}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {teams.map((team) => (
            <Link
              key={team.id}
              to={`/staff/roster/teams/${team.id}`}
              className="group bg-zinc-900/50 border border-zinc-800 rounded-sm p-5 hover:border-zinc-700 transition-colors"
              data-testid={`team-card-${team.id}`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-zinc-800 rounded-sm flex items-center justify-center text-zinc-400 group-hover:bg-amber-500/10 group-hover:text-amber-500 transition-colors">
                  <Users className="w-6 h-6" />
                </div>
                {getCategoryBadge(team.category)}
              </div>
              <div>
                <h3 className="font-semibold text-zinc-100 text-lg">{team.name}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className="font-mono text-amber-500 text-sm">{team.code}</span>
                  <span className="text-zinc-600">•</span>
                  <span className="text-zinc-500 text-sm">{team.age_group}</span>
                </div>
                {team.description && (
                  <p className="text-zinc-500 text-sm mt-2 line-clamp-2">{team.description}</p>
                )}
              </div>
              <div className="mt-4 pt-4 border-t border-zinc-800 flex items-center justify-between">
                <span className="text-xs text-zinc-500">{team.season}</span>
                <span className="text-xs text-zinc-500 group-hover:text-amber-500 flex items-center gap-1 transition-colors">
                  {t('view')} <Eye className="w-3 h-3" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">{t('addTeam')}</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Enter team details below
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateTeam}>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400">{t('name')}</Label>
                  <Input
                    value={newTeam.name}
                    onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
                    placeholder="e.g., Under 17"
                    className="bg-zinc-950 border-zinc-800"
                    required
                    data-testid="team-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400">{t('teamCode')}</Label>
                  <Input
                    value={newTeam.code}
                    onChange={(e) => setNewTeam({ ...newTeam, code: e.target.value.toUpperCase() })}
                    placeholder="e.g., U17"
                    className="bg-zinc-950 border-zinc-800 font-mono"
                    required
                    data-testid="team-code"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-zinc-400">{t('ageGroup')}</Label>
                  <Select
                    value={newTeam.age_group}
                    onValueChange={(val) => setNewTeam({ ...newTeam, age_group: val })}
                  >
                    <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="team-age-group">
                      <SelectValue placeholder="Select age group" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {AGE_GROUPS.map((ag) => (
                        <SelectItem key={ag} value={ag}>{ag}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-400">{t('category')}</Label>
                  <Select
                    value={newTeam.category}
                    onValueChange={(val) => setNewTeam({ ...newTeam, category: val })}
                  >
                    <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="team-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-400">{t('season')}</Label>
                <Input
                  value={newTeam.season}
                  onChange={(e) => setNewTeam({ ...newTeam, season: e.target.value })}
                  placeholder="e.g., 2024-2025"
                  className="bg-zinc-950 border-zinc-800"
                  required
                  data-testid="team-season"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-400">{t('description')}</Label>
                <Textarea
                  value={newTeam.description}
                  onChange={(e) => setNewTeam({ ...newTeam, description: e.target.value })}
                  className="bg-zinc-950 border-zinc-800 min-h-[80px]"
                  data-testid="team-description"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateDialog(false)}
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                {t('cancel')}
              </Button>
              <Button
                type="submit"
                disabled={isCreating || !newTeam.name || !newTeam.code || !newTeam.age_group}
                className="bg-amber-500 text-zinc-950 hover:bg-amber-400 font-bold"
                data-testid="create-team-submit"
              >
                {isCreating ? t('loading') : t('create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamListPage;
